import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './app-sidebar';
import { TopbarBusiness } from './business-switcher';
import { CrumbProvider } from './breadcrumbs';
import { CommandPalette } from './command-palette';
import { NotificationsMenu } from './notifications-menu';
import { UserMenu } from './user-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { isComposerPath, useWarmDirection } from '@/lib/direction';
import { MobileTabBar } from './mobile-tab-bar';

export function PageFallback() {
  return (
    <div className="page-gutter space-y-4 py-6" aria-busy="true" aria-label="Loading page">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-9 w-full" />
      <div className="space-y-px overflow-hidden rounded-lg border">
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="h-10 rounded-none" />
        ))}
      </div>
    </div>
  );
}

/**
 * The shell: a quiet sidebar (navigation recedes), a slim top bar with only global tools, and a content area
 * whose page header carries the title, the back link and the primary action. No breadcrumb trail: every
 * page states where it is once, in its own header.
 */
export function AppLayout() {
  const warm = useWarmDirection();
  const { pathname } = useLocation();
  const tabBar = warm && !isComposerPath(pathname);
  return (
    <CrumbProvider>
      <SidebarProvider>
        <a href="#main" className="sr-only left-3 top-3 z-50 rounded-[var(--btn-radius)] bg-btn-primary px-3 py-2 text-btn-primary-fg focus:not-sr-only focus:absolute">
          Skip to content
        </a>
        <AppSidebar />
        <SidebarInset className="min-w-0">
          <header className="no-print sticky top-0 z-30 flex h-12 shrink-0 items-center gap-1.5 border-b bg-background px-3 sm:px-4">
            <SidebarTrigger aria-label="Toggle navigation" className="text-muted-foreground" />
            <TopbarBusiness />
            <div className="flex-1" />
            <CommandPalette />
            <NotificationsMenu />
            <UserMenu />
          </header>
          <main id="main" tabIndex={-1} className={`flex-1 outline-none ${tabBar ? 'pb-20 md:pb-0' : ''}`}>
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </main>
          {tabBar && <MobileTabBar />}
        </SidebarInset>
      </SidebarProvider>
    </CrumbProvider>
  );
}
